import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, AlertTriangle, Clock, Shield, Zap, Activity,
} from "lucide-react";
import TopNav from "../components/TopNav";
import FavoritesSheet from "../components/FavoritesSheet";
import { Skeleton } from "../components/ui/skeleton";
import { fetchServerDowntime } from "../lib/api";
import { formatDuration, formatNumber } from "../lib/format";
import RegionFlag from "../components/RegionFlag";

const WINDOW_OPTIONS = [7, 30, 90];

// Severity → color + label used by badges, timeline segments, table rows.
const severityOf = (ev) => {
  if (ev.ongoing) return { label: "ONGOING", color: "var(--ping-high)" };
  if (ev.duration_min < 5) return { label: "FLAP", color: "var(--secondary-accent)" };
  if (ev.duration_min < 30) return { label: "OUTAGE", color: "var(--ping-med)" };
  return { label: "EXTENDED", color: "var(--ping-high)" };
};

const StatPill = ({ icon, label, value, sub, testId, color }) => (
  <div
    className="tac-card p-4"
    style={{ background: "var(--bg-surface)" }}
    data-testid={testId}
  >
    <span className="corner-tr" />
    <span className="corner-bl" />
    <div className="flex items-center gap-2 mb-1">
      <span style={{ color: color || "var(--primary-accent)" }}>{icon}</span>
      <span className="hud-label">{label}</span>
    </div>
    <div
      className="font-head text-2xl font-extrabold tabular-nums"
      style={{ color: color || "var(--text-primary)" }}
    >
      {value}
    </div>
    {sub && (
      <div className="font-mono-hud text-[10px] mt-1" style={{ color: "var(--text-dim)" }}>
        {sub}
      </div>
    )}
  </div>
);

const Timeline = ({ events, windowStart, windowEnd, days }) => {
  const startMs = new Date(windowStart).getTime();
  const endMs = new Date(windowEnd).getTime();
  const span = Math.max(1, endMs - startMs);

  // Produce one date tick per 1/7th of the window (so 30D → 5-day ticks).
  const tickCount = 7;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const t = startMs + (span * i) / tickCount;
    const d = new Date(t);
    return {
      pct: (i / tickCount) * 100,
      label: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    };
  });

  return (
    <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }} data-testid="downtime-timeline">
      <span className="corner-tr" />
      <span className="corner-bl" />
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="hud-label">DOWNTIME TIMELINE · LAST {days} DAYS</div>
          <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
            {events.length} {events.length === 1 ? "OUTAGE" : "OUTAGES"} IN WINDOW
          </div>
        </div>
      </div>

      {/* Timeline strip */}
      <div
        className="relative w-full"
        style={{
          height: 44,
          background: "var(--bg-elevated)",
          borderRadius: 2,
          border: "1px solid var(--border-color)",
        }}
        role="img"
        aria-label={`${events.length} downtime segments across the last ${days} days`}
      >
        {/* Baseline reference line (shows "online" state) */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: "50%",
            height: 2,
            background: "color-mix(in srgb, var(--highlight) 40%, transparent)",
            transform: "translateY(-50%)",
          }}
        />
        {/* Outage segments */}
        {events.map((ev, idx) => {
          const sMs = new Date(ev.started_at).getTime();
          const eMs = ev.ongoing ? endMs : new Date(ev.ended_at).getTime();
          const leftPct = Math.max(0, ((sMs - startMs) / span) * 100);
          const widthPct = Math.max(0.25, ((eMs - sMs) / span) * 100);
          const sev = severityOf(ev);
          return (
            <div
              key={`${ev.started_at}-${idx}`}
              className="absolute top-1 bottom-1 cursor-help transition-opacity hover:opacity-80"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                background: sev.color,
                boxShadow: `0 0 10px color-mix(in srgb, ${sev.color} 60%, transparent)`,
                minWidth: 3,
                borderRadius: 1,
              }}
              title={`${sev.label} · ${formatDuration(ev.duration_min)}\n` +
                     `Started: ${new Date(ev.started_at).toLocaleString("en-GB")}\n` +
                     (ev.ongoing ? "Still ongoing" : `Ended: ${new Date(ev.ended_at).toLocaleString("en-GB")}`)}
              data-testid={`timeline-segment-${idx}`}
            />
          );
        })}
      </div>

      {/* Tick labels */}
      <div className="relative mt-1 h-4 w-full">
        {ticks.map((t, i) => (
          <div
            key={i}
            className="absolute font-mono-hud text-[10px] tracking-widest"
            style={{
              left: `${t.pct}%`,
              transform: i === 0 ? "translateX(0)" : i === tickCount ? "translateX(-100%)" : "translateX(-50%)",
              color: "var(--text-dim)",
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs" style={{ color: "var(--text-dim)" }}>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3" style={{ background: "var(--secondary-accent)" }} />
          Flap (&lt;5m)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3" style={{ background: "var(--ping-med)" }} />
          Outage (&lt;30m)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3" style={{ background: "var(--ping-high)" }} />
          Extended (≥30m) / Ongoing
        </span>
      </div>
    </div>
  );
};

const DowntimeTable = ({ events }) => (
  <div className="tac-card p-0 overflow-hidden" style={{ background: "var(--bg-surface)" }}>
    <span className="corner-tr" />
    <span className="corner-bl" />
    <div className="p-4 pb-3">
      <div className="hud-label">EVENT LOG</div>
      <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
        {events.length === 0 ? "No events recorded" : `${events.length} outage records`}
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" data-testid="downtime-table">
        <thead>
          <tr className="border-y" style={{ borderColor: "var(--border-color)", background: "var(--bg-main)" }}>
            <th className="text-left py-2 px-3 hud-label">STARTED</th>
            <th className="text-left py-2 px-3 hud-label">ENDED</th>
            <th className="text-right py-2 px-3 hud-label">DURATION</th>
            <th className="text-left py-2 px-3 hud-label">REASON</th>
            <th className="text-left py-2 px-3 hud-label hidden md:table-cell">LAST KNOWN</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev, idx) => {
            const sev = severityOf(ev);
            return (
              <tr
                key={`${ev.started_at}-${idx}`}
                className="border-b"
                style={{ borderColor: "var(--border-color)" }}
                data-testid={`downtime-event-${idx}`}
              >
                <td className="py-2 px-3 font-mono-hud text-xs" style={{ color: "var(--text-primary)" }}>
                  {new Date(ev.started_at).toLocaleString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </td>
                <td className="py-2 px-3 font-mono-hud text-xs" style={{ color: ev.ongoing ? "var(--ping-high)" : "var(--text-dim)" }}>
                  {ev.ongoing
                    ? "— (ongoing)"
                    : new Date(ev.ended_at).toLocaleString("en-GB", {
                        day: "2-digit", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                </td>
                <td className="py-2 px-3 text-right font-mono-hud text-xs font-bold tabular-nums" style={{ color: "var(--text-primary)" }}>
                  {formatDuration(ev.duration_min)}
                </td>
                <td className="py-2 px-3">
                  <span
                    className="inline-flex items-center font-mono-hud text-[10px] font-bold px-1.5 py-0.5 border tracking-widest"
                    style={{
                      color: sev.color,
                      borderColor: sev.color,
                      background: `color-mix(in srgb, ${sev.color} 12%, transparent)`,
                      borderRadius: 2,
                    }}
                  >
                    {sev.label}
                  </span>
                  <span className="ml-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                    {ev.reason}
                  </span>
                </td>
                <td className="py-2 px-3 hidden md:table-cell font-body text-xs" style={{ color: "var(--text-dim)" }}>
                  {(ev.last_known_region || (ev.last_known_map && ev.last_known_map !== "—")) ? (
                    <span className="inline-flex items-center gap-1.5">
                      <RegionFlag region={ev.last_known_region} size={12} />
                      <span>
                        {ev.last_known_region || "—"}
                        {ev.last_known_map && ev.last_known_map !== "—" ? ` · ${ev.last_known_map}` : ""}
                      </span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const DowntimePage = () => {
  const { id: serverId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = React.useState(30);
  const [favOpen, setFavOpen] = React.useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["downtime", serverId, days],
    queryFn: () => fetchServerDowntime(serverId, days),
    refetchInterval: 60000,
    retry: 1,
  });

  if (isError) {
    return (
      <div className="min-h-screen">
        <TopNav onOpenFavorites={() => setFavOpen(true)} />
        <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
          <Link to={`/server/${encodeURIComponent(serverId)}`}
            className="inline-flex items-center gap-2 hud-caption hover:underline mb-6"
            style={{ color: "var(--primary-accent)" }}>
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SERVER
          </Link>
          <div className="tac-card p-10 text-center" style={{ background: "var(--bg-surface)" }} data-testid="downtime-error">
            <span className="corner-tr" /><span className="corner-bl" />
            <div className="hud-caption mb-2" style={{ color: "var(--ping-high)" }}>SERVER NOT FOUND</div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error?.response?.status === 404
                ? "This server id isn't tracked right now."
                : "Failed to load downtime data. Try again shortly."}
            </p>
          </div>
          <FavoritesSheet open={favOpen} onOpenChange={setFavOpen} onOpenServer={(s) => navigate(`/server/${encodeURIComponent(s.id)}`)} />
        </main>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen">
        <TopNav onOpenFavorites={() => setFavOpen(true)} />
        <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
          <Skeleton className="h-10 w-96" style={{ background: "var(--bg-elevated)" }} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20" style={{ background: "var(--bg-elevated)" }} />)}
          </div>
          <Skeleton className="h-24 mt-6" style={{ background: "var(--bg-elevated)" }} />
        </main>
      </div>
    );
  }

  const { stats, events, window_start, window_end, server_name, online } = data;

  return (
    <div className="min-h-screen" data-testid="downtime-page">
      <TopNav onOpenFavorites={() => setFavOpen(true)} />
      <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        <Link
          to={`/server/${encodeURIComponent(serverId)}`}
          data-testid="back-to-server"
          className="inline-flex items-center gap-2 hud-caption hover:underline"
          style={{ color: "var(--primary-accent)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SERVER
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="hud-caption flex items-center gap-2">
              {serverId}
              <span style={{ color: online ? "var(--highlight)" : "var(--ping-high)" }}>
                · {online ? "ONLINE" : "OFFLINE"}
              </span>
            </div>
            <h1
              className="font-head text-3xl md:text-4xl font-extrabold uppercase mt-1"
              style={{ color: "var(--text-primary)", letterSpacing: "0.02em" }}
              data-testid="downtime-title"
            >
              DOWNTIME HISTORY · {server_name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              Every tracked outage, flap and extended downtime from the last {days} days.
            </p>
          </div>

          {/* Window toggle */}
          <div className="flex items-center gap-1" data-testid="window-toggle">
            <span className="hud-label mr-2">WINDOW</span>
            {WINDOW_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                data-testid={`window-${d}d`}
                className="font-mono-hud text-xs tracking-widest px-3 py-1.5 border transition-colors"
                style={{
                  color: days === d ? "#000" : "var(--text-primary)",
                  background: days === d ? "var(--primary-accent)" : "var(--bg-elevated)",
                  borderColor: days === d ? "var(--primary-accent)" : "var(--border-strong)",
                  borderRadius: 2,
                }}
              >
                {d}D
              </button>
            ))}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill
            icon={<AlertTriangle className="w-4 h-4" />}
            label="TOTAL OUTAGES"
            value={formatNumber(stats.total_outages)}
            sub={`${stats.flap_count} ${stats.flap_count === 1 ? "flap" : "flaps"} (<5m)`}
            testId="downtime-stat-outages"
            color={stats.total_outages === 0 ? "var(--highlight)" : "var(--secondary-accent)"}
          />
          <StatPill
            icon={<Clock className="w-4 h-4" />}
            label="TOTAL DOWNTIME"
            value={formatDuration(stats.total_downtime_min) || "0m"}
            sub={`in last ${days} days`}
            testId="downtime-stat-total"
            color={stats.total_downtime_min === 0 ? "var(--highlight)" : "var(--ping-med)"}
          />
          <StatPill
            icon={<Zap className="w-4 h-4" />}
            label="LONGEST OUTAGE"
            value={stats.longest_outage_min > 0 ? formatDuration(stats.longest_outage_min) : "—"}
            sub={stats.longest_outage_min >= 30 ? "extended downtime" : stats.longest_outage_min > 0 ? "short interruption" : "no outages"}
            testId="downtime-stat-longest"
            color={stats.longest_outage_min >= 30 ? "var(--ping-high)" : stats.longest_outage_min > 0 ? "var(--secondary-accent)" : "var(--highlight)"}
          />
          <StatPill
            icon={<Shield className="w-4 h-4" />}
            label={`UPTIME · ${days}D`}
            value={stats.uptime_pct != null ? `${stats.uptime_pct.toFixed(2)}%` : "—"}
            sub={stats.mtbf_min ? `MTBF ${formatDuration(stats.mtbf_min)}` : "no failure cadence yet"}
            testId="downtime-stat-uptime"
            color={
              stats.uptime_pct == null ? "var(--text-primary)" :
              stats.uptime_pct >= 99.5 ? "var(--highlight)" :
              stats.uptime_pct >= 95 ? "var(--secondary-accent)" :
              "var(--ping-high)"
            }
          />
        </div>

        {/* Empty state */}
        {events.length === 0 ? (
          <div
            className="tac-card p-10 text-center"
            style={{ background: "var(--bg-surface)" }}
            data-testid="downtime-empty"
          >
            <span className="corner-tr" /><span className="corner-bl" />
            <Activity className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--highlight)" }} />
            <div className="hud-caption mb-2" style={{ color: "var(--highlight)" }}>ROCK SOLID</div>
            <h2 className="font-head text-2xl font-extrabold uppercase mb-2" style={{ color: "var(--text-primary)" }}>
              No downtime recorded
            </h2>
            <p className="text-sm max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
              This server has stayed online for the entire {days}-day window. No flaps, no outages, no bad days.
            </p>
          </div>
        ) : (
          <>
            <Timeline
              events={events}
              windowStart={window_start}
              windowEnd={window_end}
              days={days}
            />
            <DowntimeTable events={events} />
          </>
        )}

        <FavoritesSheet open={favOpen} onOpenChange={setFavOpen} onOpenServer={(s) => navigate(`/server/${encodeURIComponent(s.id)}`)} />
      </main>
    </div>
  );
};

export default DowntimePage;
